import { ref, useContext } from '@nuxtjs/composition-api';
import { Logger } from '@wemade-vsf/core';
import { useUser } from '@wemade-vsf/customer';
import { generateUserData } from '@wemade-vsf/customer/lib/runtime/helpers/generateUserData';
import { useUiNotification, useEvent } from '@wemade-vsf/composables';

import type { CustomerCreateInput } from '@wemade-vsf/magento-api';

interface CustomUserRegisterParams extends CustomerCreateInput {
  phone_number?: string;
  recaptchaToken?: string;
}

export function useCustomRegistration() {
  const loading = ref(false);
  const error = ref<Error | null>(null);

  const { app } = useContext();
  const { send: sendNotification } = useUiNotification();

  const { login } = useUser();

  const register = async (params: CustomUserRegisterParams): Promise<void> => {
    Logger.debug('[Magento] useUser.register', params);
    try {
      loading.value = true;

      const {
        email,
        password,
        recaptchaToken,
        ...baseData
      } = generateUserData(params);

      const { data, errors } = await app.$vsf.$magento.api.createCustomer({
        email,
        password,
        ...params.phone_number ? { phone_number: params.phone_number } : {},
        ...baseData,
      });

      if (errors) {
        const joinedErrors = errors.map((error) => error.message).join(', ');
        Logger.error(joinedErrors);
        errors.forEach((registerError, i) => sendNotification({
          icon: 'error',
          id: Symbol(`registration_error-${i}`),
          message: registerError.message,
          persist: true,
          title: 'Registration error',
          type: 'danger',
        }));
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        throw new Error(joinedErrors);
      }
      error.value = null;
      useEvent('user:register');
      await login({ user: { email, password }});
    } catch (e) {
      error.value = e as unknown as Error;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    error,
    register
  }
}